import { useCallback, useEffect, useState } from "react";

import {
  AdminLocationResponseItem,
  AdminLocationRow,
  GetAdminLocationsResponse,
  LocationCategory,
} from "@/components/export-locations-admin/types";
import { useNotifications } from "@/contexts/NotificationsContext";

import { useApi } from "../useApi";

/**
 * Data access for the admin "Transfer Locations" tab.
 *
 * Transfer locations are Iconik storages (purpose=FILES), a distinct resource
 * from export locations. Reads ALL of them (enabled + disabled) from the
 * admin-gated ``GET /admin/transfer-locations`` and persists ``enabled`` via
 * ``PATCH /admin/transfer-locations/{id}`` (optimistic, rolled back on
 * failure).
 */

const deriveCategory = (name: string): LocationCategory => {
  const n = name.toLowerCase();
  if (n.includes("evs") || n.includes("scan") || n.includes("control room")) {
    return "EVS";
  }
  if (n.includes("isg") || n.includes("truck")) return "ISG";
  if (n.includes("archive")) return "Archive";
  if (n.includes("cloud") || n.includes("dlp") || n.includes("staging") || n.includes("s3")) {
    return "Cloud";
  }
  return "Other";
};

const toRow = (loc: AdminLocationResponseItem): AdminLocationRow => ({
  id: loc.id,
  displayName: loc.displayName,
  category: deriveCategory(loc.displayName),
  enabled: loc.enabled,
  lastChangedBy: loc.lastChangedBy,
  lastChangedAt: loc.lastChangedAt,
});

const useAdminTransferLocations = () => {
  const [rows, setRows] = useState<AdminLocationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const { notifyError } = useNotifications();
  const { apiGet, apiPatch } = useApi({ isAdmin: true });

  const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet<GetAdminLocationsResponse>(
        "/admin/transfer-locations"
      );
      setRows((response?.locations ?? []).map(toRow));
    } catch (error) {
      console.error(error);
      notifyError("Locations Error", "Failed to load transfer locations");
    } finally {
      setLoading(false);
    }
  }, [apiGet, notifyError]);

  const setEnabled = useCallback(
    async (id: string, enabled: boolean) => {
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, enabled } : r))
      );
      try {
        const response = await apiPatch<{
          location: Partial<AdminLocationResponseItem>;
        }>(`/admin/transfer-locations/${id}`, { enabled });
        // Adopt the server's attribution stamp from the response -- see
        // useAdminLocations for why this can't be optimistic.
        const patched = response?.location;
        if (patched) {
          setRows((prev) =>
            prev.map((r) =>
              r.id === id
                ? {
                    ...r,
                    lastChangedBy: patched.lastChangedBy ?? r.lastChangedBy,
                    lastChangedAt: patched.lastChangedAt ?? r.lastChangedAt,
                  }
                : r
            )
          );
        }
      } catch (error) {
        console.error(error);
        setRows((prev) =>
          prev.map((r) => (r.id === id ? { ...r, enabled: !enabled } : r))
        );
        notifyError(
          "Locations Error",
          `Failed to ${enabled ? "enable" : "disable"} the transfer location`
        );
      }
    },
    [apiPatch, notifyError]
  );

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  return { rows, loading, loadLocations, setEnabled };
};

export default useAdminTransferLocations;
